import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontRobotoCondensed, theme, transientOptions } from "@product/scmp-sdk";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  ActionBar,
  CoverImage,
  Container as TopStoryContainer,
} from "~/components/content/content-item-render/variants/olympics-top-story/styles";
import {
  Container as HeaderContainer,
  LogoLink as HeaderLogoLink,
} from "~/components/olympic-widgets/header/styles";
import { Container as MedalWidgetContainer } from "~/components/olympic-widgets/medal-widget/styles";
import { Container as TodayHighlightsContainer } from "~/components/olympic-widgets/today-highlights/styles";
import {
  StyledTabs,
  TabPanelContainer,
} from "~/components/olympic-widgets/today-highlights/today-highlights-tabs/styles";
import {
  TopStories,
  Container as TopStoriesContainer,
  Header as TopStoriesHeader,
} from "~/components/olympic-widgets/top-stories/styles";

export const StyledSwiper = styled(Swiper)`
  & > .swiper-wrapper {
    block-size: auto;
  }
  & > .swiper-pagination {
    /* stylelint-disable-next-line */
    bottom: 18px !important;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    & > .swiper-wrapper {
      flex-direction: column-reverse !important;
    }
  }
`;

export const Wrapper = styled.div``;

export const MedalWidgetWrapper = styled.div`
  min-block-size: 473px;
`;

export const OlympicTodayStoriesWrapper = styled.div``;

export const OlympicTodayHighlightsWrapper = styled.div`
  min-block-size: 652px;
`;

type StyledSwiperSlideProps = {
  $isArticle?: boolean;
  $order?: number;
};
export const StyledSwiperSlide = styled(SwiperSlide, {
  ...transientOptions,
})<StyledSwiperSlideProps>`
  block-size: auto !important;
  ${props => props.theme.breakpoints.only("mobile")} {
    ${props =>
      props.$isArticle
        ? css`
            padding-block-end: 40px;
          `
        : null}
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    &:last-child {
      margin: 0 !important;
    }
  }
  ${props => props.theme.breakpoints.between("tablet", "homeDesktop")} {
    ${props =>
      props.$order
        ? css`
            order: ${props.$order};
          `
        : null}
  }

  ${MedalWidgetWrapper} {
    ${props =>
      props.$isArticle
        ? css`
            margin-block-start: 0;
          `
        : css`
            margin-block-start: 24px;
          `}
    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block-start: 0;
    }
  }

  ${OlympicTodayStoriesWrapper}, ${OlympicTodayHighlightsWrapper} {
    ${props =>
      props.$isArticle
        ? css`
            margin-block-start: 0;
          `
        : css`
            margin-block-start: 24px;
          `}
    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block-start: 24px;
    }
  }
`;

export const SlideHeader = styled.div`
  margin-block-end: 8px;
  padding-inline: 20px;

  color: #000000;
  font-size: 20px;
  font-family: ${fontRobotoCondensed};
  text-transform: uppercase;

  opacity: 0.5;
`;

const swiperStyle = css`
  ${StyledSwiper} {
    & > .swiper-wrapper {
      flex-flow: row wrap !important;
    }
  }

  ${TopStories} {
    display: flex;
    gap: 16px;
  }
`;

type ContainerProps = {
  $isArticle?: boolean;
};

const swiperTabletStyle = css`
  ${swiperStyle}
  ${StyledSwiperSlide} {
    &:last-child,
    &:first-child {
      inline-size: 50%;
    }
  }

  ${TabPanelContainer} {
    block-size: 391.17px;
  }

  ${OlympicTodayHighlightsWrapper} {
    min-block-size: 478.16px;
  }

  ${TopStoriesHeader} {
    margin-block-end: 16px;

    text-align: center;
  }

  ${TodayHighlightsContainer} {
    margin-block-end: 0;
    padding-inline-start: 24px;
    border-inline-start: 1px solid #d7d7d7;
  }
  ${HeaderContainer} {
    grid: ". logo ads" min-content/ 1fr 1fr 1fr;
  }

  ${HeaderLogoLink} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  ${Wrapper} {
    padding-inline: 20px;
  }

  ${SlideHeader} {
    text-align: center;
  }

  ${MedalWidgetContainer} {
    margin-inline-end: 24px;
  }

  & .MuiButtonBase-root {
    ${StyledTabs} {
      padding-block: 8px 8px;
    }
  }

  ${TopStoriesContainer} {
    padding-inline: 0;
  }

  ${TopStories} {
    ${TopStoryContainer} {
      border-block-end: 0;
      &:not(:last-child) {
        margin-block-end: 0;
      }
    }

    ${CoverImage} {
      aspect-ratio: 324/144;
    }

    ${ActionBar} {
      margin-block-end: 0;
    }
  }
`;

export const Container = styled.div<ContainerProps>`
  inline-size: 100%;
  max-inline-size: 704px;
  margin-inline: auto;

  border: 1px solid #d7d7d7;
  border-radius: 8px;

  overflow: hidden;

  ${props =>
    props.$isArticle
      ? css`
          ${theme.breakpoints.only("mobile")} {
            max-inline-size: 100vw;
          }
          ${theme.breakpoints.up("desktop")} {
            ${OlympicTodayHighlightsWrapper} {
              display: none;
            }
            ${TopStoryContainer} {
              &:last-child {
                border: 0;
              }
            }
          }
          ${theme.breakpoints.between("tablet", "desktop")} {
            ${swiperTabletStyle}
          }
        `
      : css`
          ${OlympicTodayHighlightsWrapper} {
            margin-block-end: -24px;
          }
          ${theme.breakpoints.between("tablet", "homeDesktop")} {
            ${swiperTabletStyle}
          }
        `}
`;
